import { ReactNode } from "react";
import DragMarquee from "./DragMarquee";

const COLORS = {
  gscClicks: "#1a73e8",
  gscImpressions: "#7c4dff",
  blue: "#3b82f6",
  orange: "#f59e0b",
};

const XLogo = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ArrowUp = ({ color }: { color: string }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M5 1.5L8.5 6H6.25v2.5h-2.5V6H1.5L5 1.5z" fill={color} />
  </svg>
);
const ArrowDown = ({ color }: { color: string }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M5 8.5L1.5 4h2.25V1.5h2.5V4H8.5L5 8.5z" fill={color} />
  </svg>
);

const rgba = (hex: string, a: number) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// Seeded pseudo-random in [0,1)
const rand = (seed: number) => {
  const x = Math.sin(seed * 999.31) * 43758.5453;
  return x - Math.floor(x);
};

/**
 * Build a realistic zigzag series that trends upward overall.
 * - Slow upward trend
 * - Daily noise (zigzag)
 * - Occasional small dips
 */
const buildZigzag = (
  pts: number,
  seedBase: number,
  opts: { startFrac?: number; endFrac?: number; noise?: number } = {}
) => {
  const startFrac = opts.startFrac ?? 0.15;
  const endFrac = opts.endFrac ?? 0.92;
  const noise = opts.noise ?? 0.12;
  const vals: number[] = [];
  for (let i = 0; i < pts; i++) {
    const t = i / (pts - 1);
    // Eased upward trend (slow start, steeper end — typical SEO growth)
    const trend = startFrac + (endFrac - startFrac) * Math.pow(t, 1.6);
    // High-frequency daily noise
    const n1 = (rand(seedBase + i) - 0.5) * noise;
    // Lower-frequency wave for small dips
    const n2 = Math.sin(i * 0.9 + seedBase) * noise * 0.35;
    // Occasional sharper dip
    const dip = rand(seedBase * 2 + i) < 0.12 ? -noise * 0.6 : 0;
    let v = trend + n1 + n2 + dip;
    if (v < 0.04) v = 0.04;
    if (v > 0.98) v = 0.98;
    vals.push(v);
  }
  return vals;
};

// Convert fractional values (0..1, 0=bottom) to SVG coords
const toCoords = (vals: number[], W: number, H: number, padY = 4): [number, number][] =>
  vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = padY + (1 - v) * (H - padY * 2);
    return [x, y];
  });

const linePathFrom = (coords: [number, number][]) =>
  coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");

const smoothPathFrom = (coords: [number, number][]) => {
  if (coords.length === 0) return "";
  let d = `M${coords[0][0]},${coords[0][1]}`;
  for (let i = 1; i < coords.length; i++) {
    const [px, py] = coords[i - 1];
    const [x, y] = coords[i];
    const cx = (px + x) / 2;
    const cy = (py + y) / 2;
    d += ` Q${px},${py} ${cx},${cy}`;
  }
  d += ` T${coords[coords.length - 1][0]},${coords[coords.length - 1][1]}`;
  return d;
};

/* ---------- Panel 1: Google Search Console (faithful recreation) ---------- */
const GscPanel = () => {
  const W = 320;
  const H = 92;
  const pts = 30;

  const clicks = buildZigzag(pts, 7.1, { startFrac: 0.08, endFrac: 0.55, noise: 0.13 });
  const impressions = buildZigzag(pts, 19.4, { startFrac: 0.18, endFrac: 0.9, noise: 0.1 });

  const cCoords = toCoords(clicks, W, H, 6);
  const iCoords = toCoords(impressions, W, H, 6);

  const xLabels = ["12/15", "1/5", "1/26", "2/16", "3/8", "3/29"];

  return (
    <PanelShell>
      {/* Top metric pills row */}
      <div className="px-2.5 pt-2.5 pb-2 flex items-center gap-1.5 flex-wrap" style={{ borderBottom: "1px solid var(--gsc-border)" }}>
        {/* Clicks pill - active blue */}
        <div className="rounded-md px-2 py-1 flex flex-col" style={{ background: COLORS.gscClicks, minWidth: 64 }}>
          <span className="text-[8px] leading-none text-white/90 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-white/90" /> Total clicks
          </span>
          <span className="text-[12px] leading-tight font-semibold text-white mt-0.5 tabular-nums">843</span>
        </div>
        {/* Impressions pill - active purple */}
        <div className="rounded-md px-2 py-1 flex flex-col" style={{ background: COLORS.gscImpressions, minWidth: 70 }}>
          <span className="text-[8px] leading-none text-white/90 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-white/90" /> Total impressions
          </span>
          <span className="text-[12px] leading-tight font-semibold text-white mt-0.5 tabular-nums">12.4K</span>
        </div>
        {/* CTR - inactive */}
        <div className="rounded-md px-2 py-1 flex flex-col" style={{ background: "var(--gsc-pill-inactive)", minWidth: 58 }}>
          <span className="text-[8px] leading-none flex items-center gap-1" style={{ color: "var(--gsc-label)" }}>
            <span className="inline-block w-2 h-2 rounded-sm border" style={{ borderColor: "var(--gsc-label)" }} /> Average CTR
          </span>
          <span className="text-[11px] leading-tight font-medium mt-0.5 tabular-nums" style={{ color: "var(--gsc-value)" }}>6.8%</span>
        </div>
        {/* Position - inactive */}
        <div className="rounded-md px-2 py-1 flex flex-col" style={{ background: "var(--gsc-pill-inactive)", minWidth: 62 }}>
          <span className="text-[8px] leading-none flex items-center gap-1" style={{ color: "var(--gsc-label)" }}>
            <span className="inline-block w-2 h-2 rounded-sm border" style={{ borderColor: "var(--gsc-label)" }} /> Average position
          </span>
          <span className="text-[11px] leading-tight font-medium mt-0.5 tabular-nums" style={{ color: "var(--gsc-value)" }}>4.2</span>
        </div>
        <div className="ml-auto text-[9px] flex items-center gap-0.5 px-1.5 py-1 rounded border" style={{ color: "var(--gsc-value)", borderColor: "var(--gsc-border)" }}>
          Daily
          <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
      </div>

      {/* Chart */}
      <div className="px-2 pt-2 pb-1">
        <svg viewBox={`0 0 ${W} ${H + 14}`} width="100%" height="106" preserveAspectRatio="none" className="block">
          {/* horizontal gridlines */}
          {[0.25, 0.5, 0.75].map((f) => (
            <line
              key={f}
              x1="0"
              x2={W}
              y1={6 + f * (H - 12)}
              y2={6 + f * (H - 12)}
              stroke="var(--gsc-grid)"
              strokeWidth="0.5"
            />
          ))}
          {/* impressions line (purple) */}
          <path d={linePathFrom(iCoords)} fill="none" stroke={COLORS.gscImpressions} strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" opacity="0.95" />
          {/* clicks line (blue) */}
          <path d={linePathFrom(cCoords)} fill="none" stroke={COLORS.gscClicks} strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" opacity="0.95" />
          {/* x-axis labels */}
          {xLabels.map((lbl, i) => {
            const x = (i / (xLabels.length - 1)) * W;
            return (
              <text
                key={lbl}
                x={x}
                y={H + 11}
                fontSize="7"
                fill="var(--gsc-label)"
                textAnchor={i === 0 ? "start" : i === xLabels.length - 1 ? "end" : "middle"}
              >
                {lbl}
              </text>
            );
          })}
        </svg>
      </div>
    </PanelShell>
  );
};

/* ---------- Panel 2: Ahrefs DR (BLUE) ---------- */
const AhrefsPanel = () => {
  const c = COLORS.blue;
  const dr = 34;
  const radius = 26;
  const circ = 2 * Math.PI * radius;
  const dash = (dr / 100) * circ;

  // Realistic zigzag bars (6 months, trending up with variance)
  const barRaw = buildZigzag(6, 3.7, { startFrac: 0.25, endFrac: 0.95, noise: 0.18 });
  const bars = barRaw.map((v) => Math.round(v * 100));
  const maxBar = Math.max(...bars);
  const barW = 28, gap = 12, chartH = 56;

  return (
    <PanelShell>
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: "1px solid var(--gsc-border)" }}>
        <span className="text-[11px] font-medium" style={{ color: "var(--gsc-value)" }}>Ahrefs · Site Explorer</span>
        <span className="text-[10px]" style={{ color: "var(--gsc-label)" }}>Overview</span>
      </div>
      <div className="flex items-center gap-4 px-4 py-3">
        <div className="relative shrink-0" style={{ width: 64, height: 64 }}>
          <svg width="64" height="64" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r={radius} fill="none" stroke="var(--gsc-border)" strokeWidth="5" />
            <circle
              cx="32" cy="32" r={radius} fill="none"
              stroke={c} strokeWidth="5" strokeLinecap="round"
              strokeDasharray={`${dash} ${circ}`}
              transform="rotate(-90 32 32)"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[9px] leading-none" style={{ color: "var(--gsc-label)" }}>DR</span>
            <span className="text-[16px] font-bold leading-none mt-0.5" style={{ color: "var(--gsc-value)" }}>{dr}</span>
          </div>
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center justify-between text-[12px]">
            <span style={{ color: "var(--gsc-label)" }}>Backlinks</span>
            <span className="font-semibold tabular-nums flex items-center gap-1" style={{ color: "var(--gsc-value)" }}>
              284 <ArrowUp color={c} />
            </span>
          </div>
          <div className="flex items-center justify-between text-[12px]">
            <span style={{ color: "var(--gsc-label)" }}>Referring domains</span>
            <span className="font-semibold tabular-nums flex items-center gap-1" style={{ color: "var(--gsc-value)" }}>
              67 <ArrowUp color={c} />
            </span>
          </div>
        </div>
      </div>
      <div style={{ borderTop: "1px solid var(--gsc-border)" }} className="px-3 pt-2 pb-2">
        <div className="text-[10px] mb-1" style={{ color: "var(--gsc-label)" }}>New backlinks · 6 mo</div>
        <svg width="100%" height={chartH} viewBox={`0 0 ${bars.length * (barW + gap)} ${chartH}`} preserveAspectRatio="none">
          {bars.map((v, i) => {
            const h = (v / maxBar) * (chartH - 4);
            return (
              <rect
                key={i}
                x={i * (barW + gap)}
                y={chartH - h}
                width={barW}
                height={h}
                rx="2"
                fill={c}
                opacity={0.55 + (i / bars.length) * 0.45}
              />
            );
          })}
        </svg>
      </div>
    </PanelShell>
  );
};

/* ---------- Panel 3: Google Analytics (ORANGE/AMBER, zigzag area) ---------- */
const GaPanel = () => {
  const c = COLORS.orange;
  const W = 300, H = 64, pts = 30;

  const vals = buildZigzag(pts, 11.8, { startFrac: 0.12, endFrac: 0.85, noise: 0.14 });
  const coords = toCoords(vals, W, H, 5);
  const linePath = smoothPathFrom(coords);
  const fillPath = `${linePath} L${W},${H} L0,${H} Z`;

  return (
    <PanelShell>
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: "1px solid var(--gsc-border)" }}>
        <span className="text-[11px] font-medium" style={{ color: "var(--gsc-value)" }}>Google Analytics</span>
        <span className="text-[10px]" style={{ color: "var(--gsc-label)" }}>Last 30 days</span>
      </div>
      <div className="px-4 py-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--gsc-label)", letterSpacing: "0.04em" }}>
            Organic Users
          </div>
          <div className="text-[22px] font-bold leading-tight tabular-nums" style={{ color: "var(--gsc-value)" }}>
            3,240
          </div>
          <div className="text-[11px] font-medium flex items-center gap-1" style={{ color: c }}>
            <ArrowUp color={c} /> +62% vs previous
          </div>
        </div>
        <div className="text-right space-y-1 shrink-0">
          <div className="text-[11px]" style={{ color: "var(--gsc-label)" }}>
            Sessions <span className="font-semibold ml-1 tabular-nums" style={{ color: "var(--gsc-value)" }}>4,180</span>
          </div>
          <div className="text-[11px]" style={{ color: "var(--gsc-label)" }}>
            Bounce rate <span className="font-semibold ml-1 tabular-nums" style={{ color: "var(--gsc-value)" }}>41%</span>
          </div>
        </div>
      </div>
      <div style={{ borderTop: "1px solid var(--gsc-border)" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="64" preserveAspectRatio="none" className="block">
          <defs>
            <linearGradient id="ga-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={rgba(c, 0.45)} />
              <stop offset="100%" stopColor={rgba(c, 0)} />
            </linearGradient>
          </defs>
          <path d={fillPath} fill="url(#ga-area)" />
          <path d={linePath} fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </PanelShell>
  );
};

const PanelShell = ({ children }: { children: ReactNode }) => (
  <div
    className="mt-4 rounded-xl overflow-hidden"
    style={{ background: "var(--gsc-bg)", border: "1px solid var(--gsc-border)" }}
  >
    {children}
  </div>
);

type Tweet = {
  name: string;
  handle: string;
  avatar: string;
  text: string;
  date: string;
  panel: ReactNode;
};

const TWEETS: Tweet[] = [
  {
    name: "Sarah Mitchell",
    handle: "sarahmitchell_biz",
    avatar: "https://i.pravatar.cc/150?img=47",
    text: "We went from page 4 to page 1 for our main keyword in about 6 weeks. OrbitRank just runs in the background and the rankings keep climbing.",
    date: "February 28, 2026",
    panel: <GscPanel />,
  },
  {
    name: "James Okafor",
    handle: "jamesokafor",
    avatar: "https://i.pravatar.cc/150?img=12",
    text: "Tried every SEO agency. Nothing worked. OrbitRank published articles consistently and our organic traffic has been growing every single week.",
    date: "March 15, 2026",
    panel: <AhrefsPanel />,
  },
  {
    name: "Priya Nair",
    handle: "priyanair_shop",
    avatar: "https://i.pravatar.cc/150?img=32",
    text: "I run a small online store and had zero organic traffic. A couple months with OrbitRank and Google is finally sending me consistent visitors every day.",
    date: "April 4, 2026",
    panel: <GaPanel />,
  },
];

const TweetCard = ({ t }: { t: Tweet }) => (
  <article
    className="shrink-0 w-full max-w-[400px] mx-auto md:max-w-none rounded-3xl p-5 border"
    style={{
      background: "var(--tweet-card-bg)",
      borderColor: "var(--tweet-card-border)",
      boxShadow: "var(--tweet-card-shadow)",
    }}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <img src={t.avatar} alt={`${t.name} avatar`} loading="lazy" className="w-11 h-11 rounded-full object-cover shrink-0" />
        <div className="min-w-0">
          <div className="font-bold text-[15px] leading-tight truncate" style={{ color: "var(--tweet-text)" }}>{t.name}</div>
          <div className="text-[13px] truncate" style={{ color: "var(--tweet-muted)" }}>@{t.handle}</div>
        </div>
      </div>
      <div style={{ color: "var(--tweet-text)" }} className="shrink-0"><XLogo /></div>
    </div>

    <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "var(--tweet-text)" }}>{t.text}</p>

    {t.panel}

    <div className="mt-3 text-[12px]" style={{ color: "var(--tweet-muted)" }}>{t.date}</div>
  </article>
);

const TweetTestimonials = () => {
  return (
    <section style={{ padding: "64px 16px", background: "var(--lo-bg)" }} className="tweet-testimonials-section">
      <style>{`
        .tweet-testimonials-section {
          --tweet-card-bg: linear-gradient(180deg, #ffffff 0%, #fcfcfc 100%);
          --tweet-card-border: rgba(0,0,0,0.04);
          --tweet-card-shadow: 0 2px 6px rgba(0,0,0,0.04), 0 8px 20px rgba(0,0,0,0.06);
          --tweet-text: #0f172a;
          --tweet-muted: #64748b;
          --gsc-bg: #ffffff;
          --gsc-border: rgba(0,0,0,0.10);
          --gsc-grid: rgba(0,0,0,0.06);
          --gsc-label: #5f6368;
          --gsc-value: #202124;
          --gsc-pill-inactive: #f1f3f4;
        }
        .dark .tweet-testimonials-section {
          --tweet-card-bg: linear-gradient(180deg, #1a1a1a 0%, #111111 100%);
          --tweet-card-border: rgba(255,255,255,0.06);
          --tweet-card-shadow: 0 2px 4px rgba(0,0,0,0.3), 0 12px 28px rgba(0,0,0,0.35);
          --tweet-text: #f5f5f5;
          --tweet-muted: #9ca3af;
          --gsc-bg: #1a1a1a;
          --gsc-border: rgba(255,255,255,0.10);
          --gsc-grid: rgba(255,255,255,0.08);
          --gsc-label: #9aa0a6;
          --gsc-value: #f5f5f5;
          --gsc-pill-inactive: #2a2a2a;
        }
        .tweet-grid-desktop { display: grid; }
        .tweet-marquee-mobile { display: none; }
        @media (max-width: 767px) {
          .tweet-grid-desktop { display: none !important; }
          .tweet-marquee-mobile {
            display: block;
            position: relative;
            margin: 0 -16px;
            overflow: hidden;
            mask-image: linear-gradient(to right, transparent 0, #000 5%, #000 95%, transparent 100%);
            -webkit-mask-image: linear-gradient(to right, transparent 0, #000 5%, #000 95%, transparent 100%);
          }
          .tweet-marquee-mobile::-webkit-scrollbar { display: none; }
          .tweet-marquee-slide {
            width: 280px;
            flex-shrink: 0;
            scroll-snap-align: center;
            display: flex;
          }
          .tweet-marquee-slide > article {
            max-width: none !important;
            width: 280px !important;
            height: 360px !important;
            padding: 16px !important;
            margin: 0 !important;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }
          .tweet-marquee-slide > article > p { flex: 0 0 auto; }
          .tweet-marquee-slide > article > div:nth-of-type(2) { /* PanelShell wrapper */
            flex: 1 1 auto;
            min-height: 0;
            overflow: hidden;
          }
        }
      `}</style>

      <h2 className="lo-section-title" style={{ textAlign: "center", margin: "0 auto 32px", maxWidth: 720 }}>
        Real results from real businesses
      </h2>

      <div className="tweet-grid-desktop max-w-6xl mx-auto grid-cols-1 md:grid-cols-3 gap-6">
        {TWEETS.map((t) => <TweetCard key={t.handle} t={t} />)}
      </div>
      <DragMarquee
        className="tweet-marquee-mobile"
        speed={22}
        style={{ scrollSnapType: "x mandatory" }}
        trackStyle={{ gap: 14, padding: "4px 12px" }}
      >
        {[...TWEETS, ...TWEETS].map((t, i) => (
          <div key={i} className="tweet-marquee-slide">
            <TweetCard t={t} />
          </div>
        ))}
      </DragMarquee>
    </section>
  );
};

export default TweetTestimonials;
